<template>
  <div>
    <transition name="fadeUpTransform">
      <div
        v-if="showPermissionBanner"
        class="notification-permission-banner"
      >
        <div class="banner-content">
          <i class="fal fa-bell-on"></i>
          <div class="banner-text">
            <strong>{{ $t("navbar.notification.permission_title") }}</strong>
            <span>{{ $t("navbar.notification.permission_body") }}</span>
          </div>
        </div>
        <div class="banner-actions">
          <button type="button" class="btn-enable" @click="enableNotifications">
            {{ $t("navbar.notification.permission_enable") }}
          </button>
          <button type="button" class="btn-dismiss" @click="dismissBanner">
            {{ $t("navbar.notification.permission_dismiss") }}
          </button>
        </div>
      </div>
    </transition>

    <main-nav></main-nav>
    <sidebar></sidebar>
    <router-view :key="$route.path"></router-view>
  </div>
</template>

<script>
import MainNav from "../components/Layouts/MainNav.vue";
import Sidebar from "../components/Layouts/Sidebar.vue";
import {
  getNotificationPermission,
  requestNotificationPermission,
  showOrderAlert,
} from "@/utils/notifications";
import {
  unlockNotificationSound,
  playNotificationSound,
} from "@/utils/notificationSound";
import {
  createNotificationSocket,
  destroyNotificationSocket,
} from "@/utils/socket";

const PERMISSION_DISMISSED_KEY = "shams_branch_notification_banner_dismissed";

export default {
  name: "Home",

  components: { MainNav, Sidebar },

  data() {
    return {
      isLoading: true,
      socket: null,
      notificationPermission: "default",
      permissionBannerDismissed: false,
    };
  },

  computed: {
    showPermissionBanner() {
      return (
        this.notificationPermission === "default" &&
        !this.permissionBannerDismissed
      );
    },
  },

  methods: {
    launch_resize_dispatch() {
      this.$store.dispatch("sideNav_module/onResize");
    },
    refreshPermissionState() {
      this.notificationPermission = getNotificationPermission();
    },
    async enableNotifications() {
      unlockNotificationSound();
      const result = await requestNotificationPermission();
      this.notificationPermission = result;
      if (result === "granted") {
        this.permissionBannerDismissed = true;
        localStorage.setItem(PERMISSION_DISMISSED_KEY, "1");
      }
    },
    dismissBanner() {
      this.permissionBannerDismissed = true;
      localStorage.setItem(PERMISSION_DISMISSED_KEY, "1");
    },
    unlockAudio() {
      unlockNotificationSound();
    },
    removeAudioUnlockListeners() {
      document.removeEventListener("click", this.unlockAudio);
      document.removeEventListener("keydown", this.unlockAudio);
    },
    handleOrderNotification(payload) {
      const orderId = payload.key_id || payload.data_id;
      const dataToPush = {
        data_id: orderId,
        key_id: orderId,
        key: payload.key || "order",
        body: payload.body,
        title: payload.title,
        created_time: this.$t("navbar.notification.just_now"),
      };

      this.$store.commit("notifications_module/pushNotification", dataToPush);
      this.$store.commit("notifications_module/triggerOrderRefresh", orderId);

      this.$nextTick(() => {
        showOrderAlert({
          router: this.$router,
          viewLabel: this.$t("navbar.notification.view_order"),
          title: payload.title,
          body: payload.body,
          orderId,
          playSound: playNotificationSound,
        });
      });
    },
    initNotificationSocket() {
      const userId = localStorage.getItem("shams_branches_dash_User_Id");
      const token = localStorage.getItem("shams_branches_dash_Token");

      destroyNotificationSocket(this.socket);
      this.socket = null;

      this.socket = createNotificationSocket({
        userId,
        token,
        onNotification: (payload) => {
          if (payload.key === "order" || payload.key_id || payload.data_id) {
            this.handleOrderNotification(payload);
          }
        },
      });
    },
  },

  mounted() {
    this.permissionBannerDismissed =
      localStorage.getItem(PERMISSION_DISMISSED_KEY) === "1";
    this.refreshPermissionState();

    this.launch_resize_dispatch();
    if (this.$store.getters.resizeEventExisit == false) {
      window.addEventListener("resize", this.launch_resize_dispatch, true);
      this.$store.commit("changeRessizeEvent");
    }
    this.$store.dispatch("notifications_module/getNotifications");
    this.initNotificationSocket();

    document.addEventListener("click", this.unlockAudio);
    document.addEventListener("keydown", this.unlockAudio);
    document.addEventListener("visibilitychange", this.refreshPermissionState);
  },

  beforeDestroy() {
    this.removeAudioUnlockListeners();
    document.removeEventListener("visibilitychange", this.refreshPermissionState);
    destroyNotificationSocket(this.socket);
    this.socket = null;
  },
};
</script>

<style lang="scss" scoped>
.notification-permission-banner {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  width: min(520px, calc(100vw - 32px));
  background: var(--cardBgColor);
  border: 1px solid rgba(0, 116, 96, 0.25);
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  .banner-content {
    display: flex;
    align-items: flex-start;
    gap: 14px;

    > i {
      font-size: 28px;
      color: var(--main, #007460);
      margin-top: 2px;
    }
  }

  .banner-text {
    display: flex;
    flex-direction: column;
    gap: 4px;

    strong {
      color: var(--blackColor);
      font-size: 15px;
    }

    span {
      color: var(--cardTitle);
      font-size: 13px;
      line-height: 1.5;
    }
  }

  .banner-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;

    button {
      border: none;
      border-radius: 8px;
      padding: 8px 16px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
    }

    .btn-enable {
      background: var(--main, #007460);
      color: #fff;
    }

    .btn-dismiss {
      background: transparent;
      color: var(--cardTitle);
    }
  }
}
</style>
